import {
  Delete,
  Download,
  DriveFileRenameOutline,
  MoreVert,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Pagination,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { adminPatientDetailApi, treatmentPlanApi } from "apis";
import ConfirmationPopup from "components/common/ConfirmationPopup";
import RenameFileModal from "./RenameFileModal";
import { useToast } from "contexts/ToastContext";
import type {
  TreatmentPlanDto,
  TreatmentPlanListResponseDto,
} from "libs/cellera-sdk";
import { useMemo, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";
import {
  formatDateForFileName,
  formatDateForFileDisplay,
  formatDateTimeForDisplay,
  formatDayHeader,
  generateFileName,
  getFileIcon,
  handleFileDownload,
} from "utils/protocol-helpers";
import ShareButton from "components/common/ShareButton";
import { isShareSupported } from "utils/share";

interface AdminProtocolsProps {
  patientId: string;
  patientName?: string;
}

interface ProtocolFile {
  type: "DOCX" | "MP3" | "PDF";
  url: string | undefined;
  fileName: string;
}

interface Protocol {
  id: string;
  date: Date;
  displayLabel: string;
  files: ProtocolFile[];
  isHiddenFromPatient: boolean;
  customName?: string;
}

interface DayGroup {
  dayKey: string;
  date: Date;
  protocols: Protocol[];
}

const PAGE_SIZE = 25;

const AdminProtocols: React.FC<AdminProtocolsProps> = ({
  patientId,
  patientName,
}) => {
  const { colors } = useCelleraTheme();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [renameTarget, setRenameTarget] = useState<{
    id: string;
    currentName: string;
  } | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{
    el: HTMLElement;
    id: string;
    name: string;
  } | null>(null);
  const [pendingVisibilityId, setPendingVisibilityId] = useState<string | null>(
    null,
  );
  const [toggleTarget, setToggleTarget] = useState<{
    treatmentPlanId: string;
    nextIsHidden: boolean;
  } | null>(null);

  const { data: protocolsData, isLoading } =
    useQuery<TreatmentPlanListResponseDto>({
      queryKey: ["admin-treatmentPlansByPatient", patientId, page],
      queryFn: () =>
        treatmentPlanApi.getTreatmentPlansByPatient({
          patientId,
          page,
          pageSize: PAGE_SIZE,
        }),
      enabled: !!patientId,
    });

  const deleteMutation = useMutation({
    mutationFn: (treatmentPlanId: string) =>
      adminPatientDetailApi.deleteTreatmentPlanForAdmin({
        treatmentPlanId,
        patientId,
      }),
    onSuccess: () => {
      setDeleteTarget(null);
      queryClient.invalidateQueries({
        queryKey: ["admin-treatmentPlansByPatient", patientId],
      });
      showSuccess("Protocol deleted successfully.");
    },
    onError: () => {
      showError("Failed to delete protocol. Please try again.");
    },
  });

  const visibilityMutation = useMutation({
    mutationFn: ({
      treatmentPlanId,
      isHiddenFromPatient,
    }: {
      treatmentPlanId: string;
      isHiddenFromPatient: boolean;
    }) =>
      adminPatientDetailApi.updateTreatmentPlanVisibilityForAdmin({
        patientId,
        treatmentPlanId,
        updateTreatmentPlanVisibilityDto: { isHiddenFromPatient },
      }),
    onMutate: (variables) => {
      setPendingVisibilityId(variables.treatmentPlanId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["admin-treatmentPlansByPatient", patientId],
      });
      showSuccess(
        variables.isHiddenFromPatient
          ? "This document is hidden from the patient"
          : "This document is visible for the patient",
      );
    },
    onError: () => {
      showError("Failed to update visibility. Please try again.");
    },
    onSettled: () => {
      setPendingVisibilityId(null);
      setToggleTarget(null);
    },
  });

  const handleToggleClick = (
    treatmentPlanId: string,
    currentIsHidden: boolean,
  ) => {
    setToggleTarget({ treatmentPlanId, nextIsHidden: !currentIsHidden });
  };

  const handleToggleConfirm = () => {
    if (!toggleTarget) return;
    visibilityMutation.mutate({
      treatmentPlanId: toggleTarget.treatmentPlanId,
      isHiddenFromPatient: toggleTarget.nextIsHidden,
    });
  };

  const renameMutation = useMutation({
    mutationFn: ({
      treatmentPlanId,
      customName,
    }: {
      treatmentPlanId: string;
      customName: string;
    }) =>
      adminPatientDetailApi.renameTreatmentPlanForAdmin({
        patientId,
        treatmentPlanId,
        renameTreatmentPlanDto: { customName },
      }),
    onSuccess: () => {
      setRenameTarget(null);
      queryClient.invalidateQueries({
        queryKey: ["admin-treatmentPlansByPatient", patientId],
      });
      showSuccess("Update successful");
    },
    onError: () => {
      showError("Failed to rename. Please try again.");
    },
  });

  // Get initials from patient name for file naming
  const nameAbbreviation = useMemo(() => {
    if (!patientName) return "XX";
    const parts = patientName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return `${parts[0].charAt(0).toUpperCase()}${parts[parts.length - 1].charAt(0).toUpperCase()}`;
    }
    return parts[0].charAt(0).toUpperCase() + "X";
  }, [patientName]);

  const protocols = useMemo<Protocol[]>(() => {
    const items: Protocol[] = [];
    const abbr = nameAbbreviation;
    const seenFileNames = new Map<string, number>();

    const getUniqueFileName = (
      abbr: string,
      dateStr: string,
      ext: "docx" | "mp3" | "pdf",
    ): string => {
      const base = generateFileName(abbr, dateStr, ext);
      const count = seenFileNames.get(base) ?? 0;
      seenFileNames.set(base, count + 1);
      return count === 0 ? base : generateFileName(abbr, dateStr, ext, count);
    };

    (protocolsData?.treatmentPlans ?? []).forEach((plan: TreatmentPlanDto) => {
      const dateStr = formatDateForFileName(plan.createdAt);
      const date = new Date(plan.createdAt);
      const files: ProtocolFile[] = [];

      // Match doctor portal fallback chain for DOCX
      const docxUrl = plan.instructionUrl ?? plan.protocolTxtUrl ?? plan.txtUrl;
      if (docxUrl) {
        files.push({
          type: "DOCX",
          url: docxUrl as string,
          fileName: getUniqueFileName(abbr, dateStr, "docx"),
        });
      }

      const pdfUrl = plan.pdfUrl ?? plan.pdfReportUrl;
      if (pdfUrl) {
        files.push({
          type: "PDF",
          url: pdfUrl,
          fileName: getUniqueFileName(abbr, dateStr, "pdf"),
        });
      }

      // Match doctor portal: MP3 only if PDF also exists
      if (pdfUrl && plan.audioUrl) {
        files.push({
          type: "MP3",
          url: plan.audioUrl,
          fileName: getUniqueFileName(abbr, dateStr, "mp3"),
        });
      }

      // Only add if there's at least one file (matches doctor portal behavior)
      if (files.length > 0) {
        items.push({
          id: plan.id,
          date,
          displayLabel: plan.customName
            ? String(plan.customName)
            : `${abbr}${dateStr}`,
          files,
          isHiddenFromPatient: plan.isHiddenFromPatient ?? false,
          customName: plan.customName ? String(plan.customName) : undefined,
        });
      }
    });

    return items;
  }, [protocolsData, nameAbbreviation]);

  const totalPages = useMemo(() => {
    const total = protocolsData?.pagination?.total ?? 0;
    return Math.ceil(total / PAGE_SIZE);
  }, [protocolsData]);

  const protocolsByDay = useMemo<DayGroup[]>(() => {
    const map = new Map<string, DayGroup>();
    protocols.forEach((p) => {
      const key = p.date.toDateString();
      if (!map.has(key)) {
        map.set(key, { dayKey: key, date: p.date, protocols: [] });
      }
      map.get(key)!.protocols.push(p);
    });
    return Array.from(map.values()).sort(
      (a, b) => b.date.getTime() - a.date.getTime(),
    );
  }, [protocols]);

  const renderFileCard = (file: ProtocolFile, date: Date) => {
    const IconComponent = getFileIcon(file.type);
    const displayDate = formatDateForFileDisplay(date);
    const iconColor = file.type === "PDF" ? "#e53935" : colors.primary.main;

    return (
      <Box
        key={file.fileName}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          p: 2,
          border: `1px solid ${colors.border.light}`,
          borderRadius: "8px",
          flex: 1,
          minWidth: 200,
        }}
      >
        <IconComponent sx={{ color: iconColor, fontSize: 36, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: colors.text.primary }}
            noWrap
          >
            {file.fileName}
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block">
            {displayDate}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {file.type} Format
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            size="small"
            sx={{
              minWidth: 0,
              p: 0.5,
              color: colors.primary.main,
              flexShrink: 0,
            }}
            onClick={() => handleFileDownload(file.url, file.fileName)}
          >
            <Download />
          </Button>
          {isShareSupported && file.url && (
            <ShareButton
              url={file.url}
              fileName={file.fileName}
              color={colors.primary.main}
            />
          )}
        </Box>
      </Box>
    );
  };

  const renderProtocolCard = (item: Protocol, index: number) => {
    const displayDateTime = formatDateTimeForDisplay(item.date);

    return (
      <Card
        key={`protocol-${item.id}-${index}`}
        sx={{
          mb: 2,
          border: `1px solid ${colors.border.light}`,
          borderRadius: "8px",
          "&:hover": { boxShadow: 2 },
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: 600, color: colors.text.primary }}
              >
                {item.displayLabel}
              </Typography>
              <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                Created on {displayDateTime}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) =>
                setMenuAnchor({
                  el: e.currentTarget,
                  id: item.id,
                  name: item.displayLabel,
                })
              }
            >
              <MoreVert />
            </IconButton>
          </Box>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
            {item.files.map((file) => renderFileCard(file, item.date))}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", mt: 2 }}>
            <Checkbox
              checked={!item.isHiddenFromPatient}
              disabled={pendingVisibilityId === item.id}
              onChange={() =>
                handleToggleClick(item.id, item.isHiddenFromPatient)
              }
              sx={{
                p: 0,
                mr: 1,
                color: colors.text.secondary,
                "&.Mui-checked": { color: colors.primary.main },
              }}
            />
            <Typography variant="body2" sx={{ color: colors.text.primary }}>
              The patient can see this document
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (protocols.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 200,
        }}
      >
        <Typography
          variant="body2"
          sx={{ color: colors.text.secondary, fontStyle: "italic" }}
        >
          No protocols found for this patient
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {protocolsByDay.map((dayGroup, dayIndex) => (
          <Box key={dayGroup.dayKey}>
            <Box
              sx={{
                mb: 3,
                pb: 2,
                borderBottom: `2px solid ${colors.border.main}`,
              }}
            >
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: colors.text.primary,
                  fontSize: "1.5rem",
                }}
              >
                {formatDayHeader(dayGroup.date)}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: colors.text.secondary, mt: 0.5 }}
              >
                {dayGroup.protocols.length}{" "}
                {dayGroup.protocols.length === 1 ? "protocol" : "protocols"}{" "}
                generated
              </Typography>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {dayGroup.protocols.map((item, index) =>
                renderProtocolCard(item, index),
              )}
            </Box>
            {dayIndex < protocolsByDay.length - 1 && (
              <Divider
                sx={{ mt: 4, mb: 2, borderColor: colors.border.light }}
              />
            )}
          </Box>
        ))}
      </Box>

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      <ConfirmationPopup
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
        title="Delete Protocol"
        message="Are you sure you want to delete this protocol for the patient? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        confirmColor="#d32f2f"
        loading={deleteMutation.isPending}
        maxWidth="sm"
        paperSx={{ borderRadius: 4 }}
      />

      <Menu
        anchorEl={menuAnchor?.el}
        open={!!menuAnchor}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            if (menuAnchor) {
              setRenameTarget({
                id: menuAnchor.id,
                currentName: menuAnchor.name,
              });
            }
            setMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <DriveFileRenameOutline fontSize="small" />
          </ListItemIcon>
          <ListItemText>Rename document</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (menuAnchor) {
              setDeleteTarget(menuAnchor.id);
            }
            setMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete document</ListItemText>
        </MenuItem>
      </Menu>

      <RenameFileModal
        open={!!renameTarget}
        currentName={renameTarget?.currentName ?? ""}
        onClose={() => setRenameTarget(null)}
        onConfirm={(newName) =>
          renameTarget &&
          renameMutation.mutate({
            treatmentPlanId: renameTarget.id,
            customName: newName,
          })
        }
        loading={renameMutation.isPending}
      />

      <ConfirmationPopup
        open={!!toggleTarget}
        onClose={() => setToggleTarget(null)}
        onConfirm={handleToggleConfirm}
        title={
          toggleTarget?.nextIsHidden
            ? "Hide from Patient"
            : "Make Visible to Patient"
        }
        message={
          toggleTarget?.nextIsHidden
            ? "Are you sure you want to hide this document from the patient? The patient will no longer be able to see this document."
            : "Are you sure you want to make this document visible to the patient? The patient will be able to see this document."
        }
        confirmText={toggleTarget?.nextIsHidden ? "Hide" : "Make Visible"}
        cancelText="Cancel"
        confirmColor={
          toggleTarget?.nextIsHidden ? colors.warning[600] : colors.primary.main
        }
        loading={visibilityMutation.isPending}
        maxWidth="sm"
        paperSx={{ borderRadius: 4 }}
      />
    </Box>
  );
};

export default AdminProtocols;
